import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  TextField
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  GetApp as GetAppIcon
} from '@mui/icons-material';
import {
  getInventoryCountRequestDetails,
  deleteInventoryCountEntry,
  cancelInventoryCountApproval,
  exportInventoryApprovalToExcel,
  cancelInventoryCountRequest
} from '../../services/inventoryCountService';
import {
  InventoryCountRequest,
  InventoryCountEntry,
  InventoryCountStatus
} from '../../types/inventoryCount';
import { formatDate } from '../../utils/dateUtils';

const InventoryCountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [request, setRequest] = useState<InventoryCountRequest | null>(null);
  const [entries, setEntries] = useState<InventoryCountEntry[]>([]);
  const [openDeleteEntryDialog, setOpenDeleteEntryDialog] = useState<boolean>(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [openCancelApprovalDialog, setOpenCancelApprovalDialog] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [processingAction, setProcessingAction] = useState<boolean>(false);
  const [actionMessage, setActionMessage] = useState<{text: string, type: 'success' | 'error'} | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteReason, setDeleteReason] = useState<string>('');

  // ترجمة حالات الجرد
  const statusLabels: Record<InventoryCountStatus, string> = {
    draft: 'مسودة',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتمل',
    approved: 'معتمد',
    cancelled: 'ملغي'
  };

  // ألوان حالات الجرد
  const statusColors: Record<InventoryCountStatus, string> = {
    draft: 'default',
    in_progress: 'info',
    completed: 'warning',
    approved: 'success',
    cancelled: 'error'
  };

  // جلب تفاصيل طلب الجرد
  const fetchRequestDetails = async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await getInventoryCountRequestDetails(id);
      if (response.success) {
        setRequest(response.request);
        setEntries(response.entries);
      } else {
        setError('حدث خطأ أثناء جلب بيانات الطلب');
      }
    } catch (err) {
      console.error('Error fetching inventory count request details:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  // جلب البيانات عند تحميل الصفحة
  useEffect(() => {
    fetchRequestDetails();
  }, [id]);

  // فتح حوار تأكيد الحذف
  const handleOpenDeleteEntryDialog = (entryId: string) => {
    setEntryToDelete(entryId);
    setOpenDeleteEntryDialog(true);
  };

  // إغلاق حوار تأكيد حذف أمر الإدخال
  const handleCloseDeleteEntryDialog = () => {
    setOpenDeleteEntryDialog(false);
    setEntryToDelete(null);
  };

  // تنفيذ حذف أمر الإدخال
  const handleConfirmDeleteEntry = async () => {
    if (!entryToDelete) return;
    
    try {
      const response = await deleteInventoryCountEntry(entryToDelete);
      if (response.success) {
        // تحديث القائمة بعد الحذف
        fetchRequestDetails();
        handleCloseDeleteEntryDialog();
      } else {
        setError('حدث خطأ أثناء حذف أمر الإدخال');
      }
    } catch (err) {
      console.error('Error deleting inventory count entry:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    }
  };

  // حذف طلب الجرد
  const handleDeleteRequest = async () => {
    if (!id) return;
    
    setProcessingAction(true);
    const response = await cancelInventoryCountRequest(id, deleteReason || undefined);
    
    if (response.success) {
      setActionMessage({
        text: 'تم حذف طلب الجرد بنجاح',
        type: 'success'
      });
      // الانتقال إلى صفحة قائمة طلبات الجرد بعد الحذف بنجاح
      setTimeout(() => {
        navigate('/inventory-count');
      }, 2000);
    } else {
      setActionMessage({
        text: response.message || 'حدث خطأ أثناء حذف طلب الجرد',
        type: 'error'
      });
    }
    
    setProcessingAction(false);
    setOpenDeleteDialog(false);
  };

  // تصدير بيانات الجرد المعتمد إلى ملف Excel
  const handleExportToExcel = async () => {
    if (!id) return;
    
    setProcessingAction(true);
    try {
      await exportInventoryApprovalToExcel(id);
      setActionMessage({
        text: 'تم تصدير بيانات الجرد بنجاح',
        type: 'success'
      });
    } catch (err: any) {
      console.error('Error exporting inventory count to Excel:', err);
      setActionMessage({
        text: err.response?.data?.message || 'حدث خطأ أثناء تصدير بيانات الجرد',
        type: 'error'
      });
    } finally {
      setProcessingAction(false);
    }
  };
  
  // التعامل مع إلغاء اعتماد الجرد
  const handleCancelApproval = async () => {
    if (!id) return;
    
    setProcessingAction(true);
    try {
      const response = await cancelInventoryCountApproval(id, cancelReason || undefined);
      if (response.success) {
        setActionMessage({
          text: 'تم إلغاء اعتماد الجرد بنجاح وإعادة كميات المخزون',
          type: 'success'
        });
        fetchRequestDetails();
      }
    } catch (err: any) {
      console.error('Error cancelling inventory count approval:', err);
      setActionMessage({
        text: err.response?.data?.message || 'حدث خطأ أثناء إلغاء اعتماد الجرد',
        type: 'error'
      });
    } finally {
      setProcessingAction(false);
      setOpenCancelApprovalDialog(false);
    }
  };

  // التحقق مما إذا كان الطلب قابل للتعديل
  const isEditable = request && ['draft', 'in_progress'].includes(request.status);

  // التحقق مما إذا كان الطلب قابل للحذف
  const isDeletable = request && ['draft', 'in_progress'].includes(request.status);

  // التحقق مما إذا كان الطلب جاهزًا للاعتماد
  const isReadyForApproval = request && request.status === 'completed';
  
  // التحقق مما إذا كان الطلب معتمداً (لعرض زر إلغاء الاعتماد)
  const isApproved = request && request.status === 'approved';

  // عرض حالة التحميل
  // عرض رسالة العملية (نجاح أو خطأ)
  const renderActionMessage = () => {
    if (!actionMessage) return null;
    
    return (
      <Alert
        severity={actionMessage.type}
        sx={{ mb: 3 }}
        onClose={() => setActionMessage(null)}
      >
        {actionMessage.text}
      </Alert>
    );
  };

  if (loading) {
    return (
      <>
        {renderActionMessage()}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            جاري تحميل بيانات طلب الجرد...
          </Typography>
        </Box>
      </>
    );
  }

  // عرض رسالة الخطأ
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // عرض رسالة في حالة عدم وجود طلب
  if (!request) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        لم يتم العثور على طلب الجرد المطلوب
      </Alert>
    );
  }


  return (
    <Box>
      {/* تفاصيل الطلب */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
            <Typography variant="h5" component="h2">
              تفاصيل طلب الجرد: {request.requestNumber}
            </Typography>
          </Box>
          <Chip
            label={statusLabels[request.status]}
            color={statusColors[request.status] as any}
            sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              المستودع:
            </Typography>
            <Typography variant="body1">
              {request.warehouseName || 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              تاريخ الإنشاء:
            </Typography>
            <Typography variant="body1">
              {formatDate(request.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold">
              منشئ الطلب:
            </Typography>
            <Typography variant="body1">
              {request.createdBy || 'غير محدد'}
            </Typography>
          </Grid>
          {request.approvedBy && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                معتمد بواسطة:
              </Typography>
              <Typography variant="body1">
                {request.approvedBy} ({formatDate(request.approvedAt || '')})
              </Typography>
            </Grid>
          )}
          {request.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">
                ملاحظات:
              </Typography>
              <Typography variant="body1" paragraph>
                {request.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* قائمة أوامر الإدخال */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h3">
            أوامر إدخال الجرد
          </Typography>
          {isEditable && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/inventory-count/${id}/entries/new`)}
            >
              إضافة أمر إدخال جديد
            </Button>
          )}
        </Box>

        {entries.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            لا توجد أوامر إدخال بعد. قم بإضافة أمر إدخال جديد للبدء.
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>رقم الإدخال</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>عدد العناصر</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>إجمالي الكميات</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>تاريخ الإنشاء</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ملاحظات</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry._id} hover>
                    <TableCell>{entry.entryNumber}</TableCell>
                    <TableCell>{entry.items.length}</TableCell>
                    <TableCell>
                      {entry.items.reduce((total, item) => total + Math.abs(item.quantity || 0), 0)} قطعة
                    </TableCell>
                    <TableCell>{formatDate(entry.createdAt)}</TableCell>
                    <TableCell>{entry.notes || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="عرض التفاصيل">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => navigate(`/inventory-count/entries/${entry._id}`)}
                          >
                            <AssignmentIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {isEditable && (
                          <>
                            <Tooltip title="تعديل">
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() => navigate(`/inventory-count/entries/${entry._id}/edit`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="حذف">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleOpenDeleteEntryDialog(entry._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              {/* صف الإجمالي */}
              <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>الإجمالي</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {entries.reduce((total, entry) => total + entry.items.length, 0)}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  {entries.reduce((total, entry) =>
                    total + entry.items.reduce((entryTotal, item) => entryTotal + Math.abs(item.quantity || 0), 0), 0
                  )} قطعة
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
              </TableRow>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* أزرار الإجراءات */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/inventory-count')}
          >
            العودة إلى قائمة طلبات الجرد
          </Button>
          
          {isDeletable && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDeleteDialog(true)}
            >
              حذف طلب الجرد
            </Button>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isReadyForApproval && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => navigate(`/inventory-count/${id}/approve`)}
              disabled={entries.length === 0}
            >
              اعتماد الجرد
            </Button>
          )}
          
          {isApproved && (
            <>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setOpenCancelApprovalDialog(true)}
              >
                إلغاء اعتماد الجرد
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<GetAppIcon />}
                onClick={() => handleExportToExcel()}
              >
                تصدير لملف Excel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* حوار تأكيد حذف طلب الجرد */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        aria-labelledby="delete-request-dialog-title"
      >
        <DialogTitle id="delete-request-dialog-title">تأكيد حذف طلب الجرد</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف طلب الجرد؟ سيؤدي ذلك إلى حذف جميع أوامر الإدخال المرتبطة به. لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="delete-reason"
            label="سبب الحذف (اختياري)"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} color="primary">
            إلغاء
          </Button>
          <Button
            onClick={handleDeleteRequest}
            color="error"
            variant="contained"
            disabled={processingAction}
          >
            {processingAction ? 'جاري المعالجة...' : 'تأكيد الحذف'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار تأكيد حذف أمر الإدخال */}
      <Dialog
        open={openDeleteEntryDialog}
        onClose={handleCloseDeleteEntryDialog}
        aria-labelledby="delete-entry-dialog-title"
      >
        <DialogTitle id="delete-entry-dialog-title">تأكيد حذف أمر الإدخال</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف أمر الإدخال؟ لا يمكن التراجع عن هذا الإجراء.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteEntryDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleConfirmDeleteEntry} color="error" variant="contained">
            تأكيد الحذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إلغاء اعتماد الجرد */}
      <Dialog
        open={openCancelApprovalDialog}
        onClose={() => setOpenCancelApprovalDialog(false)}
        aria-labelledby="cancel-approval-dialog-title"
      >
        <DialogTitle id="cancel-approval-dialog-title">تأكيد إلغاء اعتماد الجرد</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            هل أنت متأكد من رغبتك في إلغاء اعتماد الجرد؟ سيؤدي ذلك إلى إعادة كميات المخزون إلى ما كانت عليه قبل الاعتماد.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="cancel-reason"
            label="سبب إلغاء الاعتماد"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelApprovalDialog(false)} color="primary">
            إلغاء
          </Button>
          <Button
            onClick={handleCancelApproval}
            color="error"
            variant="contained"
            disabled={processingAction}
          >
            {processingAction ? 'جاري المعالجة...' : 'تأكيد إلغاء الاعتماد'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryCountDetails;